<template>
  <div>
    <!-- Direct Navigation -->
    <direct-navigation-card
      entityType="users"
      basePath="/admin/identity/users"
    />

    <!-- Filters -->
    <the-search-filter
      i18nPrefix="users"
      :loading="state.loading"
      :searchFilter="state.searchFilter"
      @clear="clearSearchFilter"
      @filterInput="param => updateInput(param)"
      @search="() => reloadUsers()"
      @urlFiltersInitialized="initializeFiltersFromUrl"
    />

    <va-card>
      <!-- Title -->
      <va-card-title>
        {{ $t("users.title") }}
      </va-card-title>

      <!-- Table -->
      <va-card-content>
        <div class="va-table-responsive">
          <table class="va-table va-table--striped va-table--hoverable">
            <thead>
              <th>{{ $t("users.email") }}</th>
              <th>{{ $t("users.name") }}</th>
            </thead>
            <tbody>
              <tr v-for="(user, index) in state.users" :key="index">
                <td>
                  <router-link :to="{ path: userLink(user.id) }">
                    {{ user.email }}
                  </router-link>
                  <button-clipboard-copy
                    :field="$t('users.email')"
                    :value="user.email"
                  />
                </td>
                <td>{{ user.name }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </va-card-content>
      <va-card-content class="load-more">
        <va-button v-if="canLoadMore()" @click="loadNextUsers()">
          {{ $t("users.loadMore") }}
        </va-button>
      </va-card-content>
    </va-card>
  </div>
</template>

<style scoped>
.va-table {
  width: 100%;
}

.load-more {
  text-align: center;
}
</style>

<script setup>
import { reactive, onMounted, inject } from "vue";
import { callFetchUsers } from "@/api/identity";
import ButtonClipboardCopy from "@/components/ButtonClipboardCopy.vue";
import TheSearchFilter from "@/components/TheSearchFilter.vue";
import DirectNavigationCard from "@/components/utils/DirectNavigationCard.vue";
import { userLink } from "@/utils/identity";

const toasts = inject("toasts");

const DEFAULT_PER_PAGE = 100;

const createSearchFilter = () => ({
  email: { value: undefined, acceptedValues: "string" },
  search: { value: undefined, acceptedValues: "string", label: "name" }
});

const state = reactive({
  users: [],
  searchFilter: createSearchFilter(),
  loading: false,
  nextPage: null,
  perPage: DEFAULT_PER_PAGE
});

let latestRequestToken = 0;

const isLatestRequest = token => token === latestRequestToken;

const mapUser = item => ({
  id: item?.id,
  email: item?.email,
  name: item?.displayName
});

let filtersInitializedFromUrl = false;

/**
 * Clear search filter values
 */
function clearSearchFilter(clearedFilters) {
  if (clearedFilters) {
    // Filters already cleared by URL sync
    state.searchFilter = clearedFilters;
  } else {
    // Fallback for non-URL sync mode
    Object.keys(state.searchFilter).forEach(key => {
      state.searchFilter[key].value = undefined;
    });
  }

  reloadUsers();
}

/**
 * Initialize filters from URL
 */
function initializeFiltersFromUrl(urlFilters) {
  state.searchFilter = urlFilters;
  filtersInitializedFromUrl = true;
  reloadUsers();
}

/**
 * Update a searchFilter property's value
 * @param {Object} param
 * @param {string} param.inputKey
 * @param {any} param.value - depends on which field is updated
 */
function updateInput({ inputKey, value }) {
  state.searchFilter[inputKey].value = value;
}

function resetUsers() {
  state.nextPage = null;
  state.users = [];
}

function buildUserParams(page) {
  const params = {
    count: state.perPage
  };

  if (page !== null && page !== undefined) {
    params.page = page;
  }

  Object.keys(state.searchFilter).forEach(key => {
    params[key] = state.searchFilter[key].value;
  });

  return params;
}

/**
 * Create the parameters object for the request and call it.
 * Then calculate the page properties's values.
 * @param {number} nextPage - Next page cursor
 */
async function loadUsers(page = null) {
  const requestToken = ++latestRequestToken;
  state.loading = true;

  try {
    const resp = await callFetchUsers({ params: buildUserParams(page) });
    if (!isLatestRequest(requestToken)) {
      return;
    }

    const items = resp?.data?.items ?? [];
    const mappedUsers = items.map(mapUser);

    state.users = page ? [...state.users, ...mappedUsers] : mappedUsers;
    state.nextPage = resp?.data?.nextPage ?? null;
  } catch (err) {
    if (isLatestRequest(requestToken)) {
      toasts?.error(err);
    }
  } finally {
    if (isLatestRequest(requestToken)) {
      state.loading = false;
    }
  }
}

async function reloadUsers() {
  resetUsers();
  await loadUsers();
}

/**
 * Loads the next portion of users.
 */
async function loadNextUsers() {
  return await loadUsers(state.nextPage);
}

function canLoadMore() {
  return !!state.nextPage;
}

onMounted(async () => {
  if (!filtersInitializedFromUrl) {
    await loadUsers();
  }
});
</script>
